import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import SidebarLinkGroup from "./SidebarLinkGroup";
import Logo from "../../assets/images/logo/new_logo.png";
import { Icon } from "@iconify/react";
import "./sidebar.scss";

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef(null);
  const sidebar = useRef(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });
  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector("body")?.classList.add("sidebar-expanded");
    } else {
      document.querySelector("body")?.classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  const closeWindow = () => {};
  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-999 flex h-screen w-72.5 flex-col overflow-y-hidden bg-black duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
      style={{ userSelect: "none" }}
    >
      {/* <!-- SIDEBAR HEADER --> */}
      <div className="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5">
        <div className="absolute top-[2px] left-[10px] flex text-white items-center user-select-none drag-region">
          <Icon
            icon="material-symbols:brightness-auto-rounded"
            onDoubleClick={closeWindow}
          />
          <h4 className="text-white text-lg font-semibold mx-2">Automatiq</h4>
        </div>
        <NavLink to="/">
          <img src={Logo} alt="Logo" />
        </NavLink>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="block lg:hidden"
        >
          <svg
            className="fill-current"
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z"
              fill=""
            />
          </svg>
        </button>
      </div>
      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
          <div>
            <h3 className="mb-4 ml-4 text-sm font-semibold text-bodydark2">
              MENU
            </h3>
            <ul className="mb-6 flex flex-col gap-1.5">
              <SidebarLinkGroup activeCondition={pathname.includes("account")}>
                {(handleClick, open) => {
                  if (pathname.includes("account")) {
                    open = true;
                  }
                  return (
                    <>
                      <NavLink
                        to="#"
                        className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                          (pathname === "/" || pathname.includes("account")) &&
                          "bg-graydark dark:bg-meta-4"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          sidebarExpanded
                            ? handleClick()
                            : setSidebarExpanded(true);
                        }}
                      >
                        <Icon icon="mdi:account-group" className="text-title-lg"/>
                        Account
                        <svg
                          className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${
                            open && "rotate-180"
                          }`}
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                            fill=""
                          />
                        </svg>
                      </NavLink>
                      <div
                        className={`translate transform overflow-hidden ${
                          !open && "hidden"
                        }`}
                      >
                        <ul className="mt-4 mb-5.5 flex flex-col gap-2.5 pl-6">
                          <li>
                            <NavLink
                              to="/account/list"
                              className={({ isActive }) =>
                                "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white " +
                                (isActive && "!text-white")
                              }
                            >
                              All Accounts
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              to="/account/detail"
                              className={({ isActive }) =>
                                "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white " +
                                (isActive && "!text-white")
                              }
                            >
                              View Account
                            </NavLink>
                          </li>
                        </ul>
                      </div>
                    </>
                  );
                }}
              </SidebarLinkGroup>
              <li>
                <NavLink
                  to="/order"
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                    pathname.includes("order") && "bg-graydark dark:bg-meta-4"
                  }`}
                >
                 <Icon icon="lsicon:place-order-outline" className="text-title-lg"/>
                  Order
                </NavLink>
              </li>
              {/* <li>
                <NavLink
                  to="/tickets"
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                    pathname.includes("tickets") && "bg-graydark dark:bg-meta-4"
                  }`}
                >
                  <svg
                    className="svg-icon"
                    width="18"
                    height="20"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M898.133333 855.466667h-768c-46.933333 0-85.333333-38.4-85.333333-85.333334v-177.066666c0-12.8 8.533333-21.333333 21.333333-21.333334 10.666667 0 21.333333 8.533333 21.333334 21.333334v177.066666c0 23.466667 19.2 42.666667 42.666666 42.666667h768c23.466667 0 42.666667-19.2 42.666667-42.666667v-168.533333c0-12.8 8.533333-21.333333 21.333333-21.333333s21.333333 8.533333 21.333334 21.333333v168.533333c0 46.933333-38.4 85.333333-85.333334 85.333334zM960 443.733333c-12.8 0-21.333333-8.533333-21.333333-21.333333v-179.2c0-23.466667-19.2-42.666667-42.666667-42.666667H128c-23.466667 0-42.666667 19.2-42.666667 42.666667v172.8c0 10.666667-10.666667 21.333333-21.333333 21.333333-12.8 0-21.333333-10.666667-21.333333-21.333333v-172.8c0-46.933333 38.4-85.333333 85.333333-85.333333h768c46.933333 0 85.333333 38.4 85.333333 85.333333v179.2c0 10.666667-8.533333 21.333333-21.333333 21.333333z"
                      fill="white"
                    />
                    <path
                      d="M960 622.933333c-61.866667 0-110.933333-49.066667-110.933333-110.933333 0-61.866667 49.066667-110.933333 110.933333-110.933333v42.666666c-38.4 0-68.266667 29.866667-68.266667 68.266667s29.866667 68.266667 68.266667 68.266667v42.666666zM70.4 614.4l-8.533333-42.666667c32-6.4 70.4-36.266667 70.4-66.133333 0-32-23.466667-59.733333-55.466667-66.133333l8.533333-42.666667c51.2 10.666667 89.6 55.466667 89.6 108.8-2.133333 55.466667-53.333333 98.133333-104.533333 108.8z"
                      fill="white"
                    />
                    <path
                      d="M76.8 439.466667c-4.266667 0-8.533333-2.133333-12.8-2.133334v-42.666666c8.533333 0 14.933333 0 21.333333 2.133333l-8.533333 42.666667zM712.533333 296.533333c0 10.666667-8.533333 21.333333-21.333333 21.333334s-21.333333-10.666667-21.333333-21.333334V192c0-12.8 8.533333-21.333333 21.333333-21.333333s21.333333 8.533333 21.333333 21.333333v104.533333zM712.533333 480c0 12.8-8.533333 21.333333-21.333333 21.333333s-21.333333-8.533333-21.333333-21.333333v-104.533333c0-10.666667 8.533333-21.333333 21.333333-21.333334s21.333333 10.666667 21.333333 21.333334v104.533333zM712.533333 667.733333c0 10.666667-8.533333 21.333333-21.333333 21.333334s-21.333333-8.533333-21.333333-21.333334v-104.533333c0-12.8 8.533333-21.333333 21.333333-21.333333s21.333333 8.533333 21.333333 21.333333v104.533333zM714.666667 832c0 12.8-8.533333 21.333333-21.333334 21.333333-10.666667 0-21.333333-8.533333-21.333333-21.333333v-81.066667c0-10.666667 8.533333-21.333333 21.333333-21.333333 10.666667 0 21.333333 10.666667 21.333334 21.333333V832zM552.533333 341.333333H247.466667c-10.666667 0-21.333333 8.533333-21.333334 21.333334s10.666667 21.333333 21.333334 21.333333h302.933333c12.8 0 21.333333-8.533333 21.333333-21.333333s-8.533333-21.333333-19.2-21.333334zM548.266667 494.933333H245.333333c-10.666667 0-21.333333 8.533333-21.333333 21.333334 0 10.666667 10.666667 21.333333 21.333333 21.333333h302.933334c10.666667 0 21.333333-10.666667 21.333333-21.333333 0-12.8-8.533333-21.333333-21.333333-21.333334zM556.8 637.866667H253.866667c-12.8 0-21.333333 10.666667-21.333334 21.333333 0 12.8 8.533333 21.333333 21.333334 21.333333h302.933333c12.8 0 21.333333-8.533333 21.333333-21.333333 0-10.666667-8.533333-21.333333-21.333333-21.333333z"
                      fill="white"
                    />
                  </svg>
                  Tickets
                </NavLink>
              </li> */}
              <li>
                <NavLink
                  to="/settings"
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                    pathname.includes("settings") &&
                    "bg-graydark dark:bg-meta-4"
                  }`}
                >
                  <Icon icon="tdesign:setting-1"  className="text-title-lg"/>
                  Settings
                </NavLink>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
